/**
 * watch-budget — how accepting a drift changes the size of Continuous watch,
 * for the queue row, the expanded drift detail and the "Waiting to apply"
 * notice (drift tier marker work, 2026-11-04).
 *
 * Continuous watch holds at most `instanceCeiling` settings (TcmBudget
 * .MaxMonitoredInstances, 200). Two accept paths change its size, and the
 * server only checks the ceiling at Apply (TcmBaselineWriter, the
 * `appendedCount > 0` guard, a 409 past it), over the WHOLE staged batch:
 *   - a new policy from the creation detector folds IN: +1, or 0 when the
 *     watch already holds it (the fold leg's adopted-skip);
 *   - a retire row (DeletionDetector / SnapshotDiff `Removed`) drops the
 *     matching node OUT: -1, or 0 when the policy was never watched (the
 *     writer's retire-already-absent close).
 * Every other row (a watched setting's change, a Full capture
 * acknowledgement, a SharePoint & OneDrive row) leaves the size alone.
 *
 * Inputs come from the coverage summary the tab already loads
 * (GET /tcm/coverage/{tenantId}): `watchedInstanceCount` is the active
 * watch's MonitoredInstanceCount and `instanceCeiling` is the same constant
 * the writer enforces, so the preview follows the writer's own arithmetic.
 * Because Apply checks the batch, a single row's line counts what is already
 * waiting to apply (`stagedNet`). The ceiling deliberately ignores other
 * monitors in the tenant (the capacity scan's `guardianInstanceCeiling`),
 * because the writer ignores them too; Graph rejects that case at the
 * re-baseline (follow-up in the tier-choice plan).
 *
 * Fail quiet: whenever a number is unknown, the composers return null and the
 * surface shows nothing rather than a guessed count (CLAUDE.md no-fabrication).
 * Customer words only (same jargon sweep as drift-decision-copy).
 */

import type { TcmCoverageSummary } from '@/types/tcm'
import { isCreationDetectorRow, isDeletionDetectorRow, type DriftPipelineSource } from './drift-decision-copy'

/** The size of Continuous watch now, plus the watched names per resource type. */
export interface WatchRoom {
  /** Settings under Continuous watch now. */
  watched: number
  /** The most settings Continuous watch may hold (what Apply enforces). */
  ceiling: number
  /**
   * Lower-cased watched names keyed by lower-cased resource type; a type absent
   * from the map is not watched at all. Null when any group's watch list could
   * not be read, so "not watched" is never inferred from a read failure.
   */
  namesByType: Map<string, Set<string>> | null
}

/** The fields of a drift row the delta needs. */
export interface WatchDeltaRow {
  source: 'DSC' | 'TCM'
  pipelineSource?: DriftPipelineSource | null
  driftType?: string | null
  resourceType?: string | null
  /** The display name the row carries (bare or DSC-wrapped). */
  resourceName?: string | null
}

/**
 * Null unless there is an active watch with a real count and ceiling (the
 * writer needs only an active watch, so `monitoringActive` isn't consulted).
 */
export function watchRoomFrom(coverage: TcmCoverageSummary | null | undefined): WatchRoom | null {
  if (!coverage) return null
  const watched = coverage.watchedInstanceCount
  const ceiling = coverage.instanceCeiling
  if (typeof watched !== 'number' || watched < 0) return null
  if (typeof ceiling !== 'number' || ceiling <= 0) return null

  let namesByType: Map<string, Set<string>> | null = new Map()
  let tallied = 0
  for (const group of coverage.groups ?? []) {
    if (group.watched === null) {
      namesByType = null
      break
    }
    if (group.watched !== true) continue
    tallied += group.watchedInstanceCount ?? 0
    for (const t of group.types ?? []) {
      const key = t.resourceType.toLowerCase()
      const set = namesByType.get(key) ?? new Set<string>()
      for (const n of t.names) set.add(n.trim().toLowerCase())
      namesByType.set(key, set)
    }
  }
  // The coverage tally skips watched resources whose type is outside the
  // catalog. When the groups don't add up to the watch's own count, a type
  // missing from the map might still be watched, so stop inferring "not watched".
  if (namesByType && tallied !== watched) namesByType = null
  return { watched, ceiling, namesByType }
}

/**
 * Mirror of TcmInstanceNameHelper.TryStripDscWrappedName: strip a leading
 * `<DscResourceName>-` only when the prefix looks like a DSC resource name
 * (3+ ASCII letters/digits, upper-case first), so `ca-policy-1` keeps its name.
 */
export function stripDscWrapper(name: string): string | null {
  const hyphen = name.indexOf('-')
  if (hyphen <= 0 || hyphen === name.length - 1) return null
  const prefix = name.slice(0, hyphen)
  if (prefix.length < 3 || !/^[A-Z][A-Za-z0-9]*$/.test(prefix)) return null
  return name.slice(hyphen + 1)
}

/** True when the watch holds a node the writer would match to this name. */
function isNameWatched(names: Set<string>, rawName: string): boolean {
  const name = rawName.trim()
  if (!name) return false
  const bare = stripDscWrapper(name) ?? name
  return names.has(name.toLowerCase()) || names.has(bare.toLowerCase())
}

/**
 * How many settings accepting this row adds to (+1) or takes off (-1)
 * Continuous watch; 0 when it leaves the size alone; null when that can't be
 * known from the coverage read (a retire whose watched names are unreadable,
 * or whose type is watched but the name isn't recognised). A new policy the
 * watch already holds is 0, as the fold leg skips it; with the names
 * unreadable it counts +1, the outcome for every policy not yet adopted.
 */
export function watchDelta(row: WatchDeltaRow, room: WatchRoom | null): number | null {
  if (row.source !== 'TCM') return 0
  if (isCreationDetectorRow(row)) {
    const names = room?.namesByType?.get((row.resourceType ?? '').toLowerCase())
    return names && isNameWatched(names, row.resourceName ?? '') ? 0 : 1
  }
  if (!isDeletionDetectorRow(row)) return 0
  if (!room?.namesByType) return null
  const names = room.namesByType.get((row.resourceType ?? '').toLowerCase())
  // The type isn't watched, so the retire closes the row with no write.
  if (!names) return 0
  return isNameWatched(names, row.resourceName ?? '') ? -1 : null
}

function settings(n: number): string {
  return n === 1 ? 'setting' : 'settings'
}

function policies(n: number): string {
  return n === 1 ? '1 policy' : `${n} policies`
}

/**
 * The net change the staged batch makes to Continuous watch (sum of its
 * deltas); null when any is unknown or the caller couldn't load the whole
 * staged queue. An empty batch is 0.
 */
export function stagedWatchNet(deltas: readonly (number | null)[] | null): number | null {
  if (!deltas || deltas.some((d) => d === null)) return null
  return deltas.reduce<number>((sum, d) => sum + (d ?? 0), 0)
}

/**
 * One line for the queue row and the drift detail: what accepting this row
 * does to Continuous watch. Apply checks the whole batch, so the count starts
 * from what is already waiting to apply (`stagedNet`); null there means the
 * staged batch is unknown, and the line stays silent rather than state a
 * count that could be wrong. Null too when the size doesn't change or isn't
 * known.
 */
export function watchChangeLine(
  delta: number | null,
  room: WatchRoom | null,
  stagedNet: number | null = 0,
): string | null {
  if (!room || delta === null || delta === 0 || stagedNet === null) return null
  const before = room.watched + stagedNet
  const after = before + delta
  const staged = stagedNet !== 0
  const of = `of ${room.ceiling} ${settings(room.ceiling)}`
  if (delta > 0 && after > room.ceiling) {
    const subject = staged ? "With what's waiting to apply, it is" : 'It is'
    return `Make room on Continuous watch first. ${subject} at ${before} ${of}, so Apply refuses a new policy. Retire a removed policy or narrow the scope on the Monitoring tab.`
  }
  const span = `${before} → ${after} ${of}${staged ? " with what's waiting to apply" : ''}`
  return delta > 0
    ? `Accepting adds this policy to Continuous watch, ${span}.`
    : `Accepting takes this policy off Continuous watch, ${span}.`
}

/** The staged batch's effect on Continuous watch, for the "Waiting to apply" notice. */
export interface StagedWatchEffect {
  /** True when Apply would refuse the batch: it adds policies past the ceiling. */
  refused: boolean
  /** The action that unblocks Apply, for the notice's title; null unless refused. */
  headline: string | null
  /** One line with the before and after counts, for the notice's body. */
  line: string
}

/**
 * The staged batch's net effect on Continuous watch. `deltas` must cover EVERY
 * staged continuous-watch row (the caller passes null when only part of the
 * staged queue is loaded); any null delta makes the whole effect unknown.
 * Adds net against retires, as at Apply: the writer drops the retired nodes
 * before it checks the ceiling, and checks it only when the batch adds one.
 */
export function stagedWatchEffect(
  deltas: readonly (number | null)[] | null,
  room: WatchRoom | null,
): StagedWatchEffect | null {
  if (!room || !deltas) return null
  if (deltas.some((d) => d === null)) return null
  const adds = deltas.filter((d) => (d ?? 0) > 0).length
  const removes = deltas.filter((d) => (d ?? 0) < 0).length
  if (adds === 0 && removes === 0) return null

  const after = room.watched + adds - removes
  const span = `${room.watched} → ${after} of ${room.ceiling} ${settings(room.ceiling)}`
  if (adds > 0 && after > room.ceiling) {
    const over = after - room.ceiling
    return {
      refused: true,
      headline: `Undo ${over === 1 ? '1 new policy' : `${over} new policies`} before you apply.`,
      line: `This batch would take Continuous watch to ${after} of ${room.ceiling} ${settings(room.ceiling)}, and Apply refuses it.`,
    }
  }
  const line =
    adds > 0 && removes > 0
      ? `Applying adds ${policies(adds)} to Continuous watch and takes ${removes} off, ${span}.`
      : adds > 0
        ? `Applying adds ${policies(adds)} to Continuous watch, ${span}.`
        : `Applying takes ${policies(removes)} off Continuous watch, ${span}.`
  return { refused: false, headline: null, line }
}
